import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TourSpotlight } from "./TourSpotlight";
import { TourTooltip } from "./TourTooltip";
import type { TourStep } from "./tourSteps";

// `lg` in tailwind = 1024px. Below that the sidebar is a drawer, so any
// step that targets a sidebar item won't be reachable. We fall back to a
// centered modal-style tooltip in those cases — simpler than driving the
// drawer open programmatically and chasing the focus around.
const MOBILE_BREAKPOINT = 1024;
const TARGET_RETRY_INTERVAL = 80;
const TARGET_GIVE_UP_AFTER = 2000;

export type Rect = { top: number; left: number; width: number; height: number };

function readRect(selector: string): Rect | null {
  const el = document.querySelector(selector) as HTMLElement | null;
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

export function TourOverlay({ step }: { step: TourStep }) {
  const [rect, setRect] = useState<Rect | null>(null);
  const [gaveUp, setGaveUp] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < MOBILE_BREAKPOINT;
  });
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Lock scroll + remember focus so we can restore it when the tour closes.
  useEffect(() => {
    previouslyFocused.current =
      (document.activeElement as HTMLElement | null) ?? null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
      try {
        previouslyFocused.current?.focus?.();
      } catch {
        /* ignore */
      }
    };
  }, []);

  // Track viewport size so resizing across the breakpoint reflows the tour.
  useEffect(() => {
    const onResize = () =>
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Reset measurement state on step change so a stale rect from the
  // previous step can't flash before the new one is measured.
  useEffect(() => {
    setRect(null);
    setGaveUp(false);
  }, [step.id]);

  // Retry-measure the target. Steps without a selector or steps whose
  // placement is "center" deliberately skip this entirely.
  useEffect(() => {
    const selector = step.targetSelector;
    if (!selector) return;
    if (step.placement === "center") return;
    if (isMobile) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const startedAt = Date.now();

    const tick = () => {
      if (cancelled) return;
      const r = readRect(selector);
      if (r) {
        const el = document.querySelector(selector) as HTMLElement | null;
        const offscreen =
          r.top < 0 ||
          r.top + r.height > window.innerHeight ||
          r.left < 0 ||
          r.left + r.width > window.innerWidth;
        if (el && offscreen) {
          el.scrollIntoView({ block: "center", behavior: "smooth" });
          // Give the browser one frame to settle the scroll, then re-read.
          requestAnimationFrame(() => {
            if (cancelled) return;
            const r2 = readRect(selector) ?? r;
            setRect(r2);
          });
          return;
        }
        setRect(r);
        return;
      }
      if (Date.now() - startedAt > TARGET_GIVE_UP_AFTER) {
        setGaveUp(true);
        return;
      }
      timer = setTimeout(tick, TARGET_RETRY_INTERVAL);
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [step.id, step.targetSelector, step.placement, isMobile]);

  // Once we have a rect, keep it fresh against layout changes (sidebar
  // resize, content reflow, devtools toggle, etc.).
  useEffect(() => {
    if (!rect || !step.targetSelector || isMobile) return;
    const el = document.querySelector(step.targetSelector) as HTMLElement | null;
    if (!el) return;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const reflow = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        const r = readRect(step.targetSelector!);
        if (r) setRect(r);
      }, 100);
    };
    const ro = new ResizeObserver(reflow);
    ro.observe(el);
    window.addEventListener("resize", reflow);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", reflow);
      if (timeout) clearTimeout(timeout);
    };
  }, [rect, step.targetSelector, isMobile]);

  const useCentered =
    step.placement === "center" ||
    !step.targetSelector ||
    gaveUp ||
    isMobile;
  const effectiveRect = useCentered ? null : rect;

  // Render order: dim layer always; tooltip waits until we know whether
  // we're centered or have the rect — never both at once, never flicker.
  return createPortal(
    <>
      <TourSpotlight rect={effectiveRect} />
      {(useCentered || rect) && (
        <TourTooltip step={step} rect={effectiveRect} />
      )}
    </>,
    document.body,
  );
}
